import React, { useState, useEffect } from 'react';
import { 
  Modal, Form, Input, InputNumber, Select, Upload, Switch,
  Button, Tabs, Spin, notification, Tag
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import productService from '../../../../services/productService';
import categoryService from '../../../../services/categoryService';
import config from '../../../../config';
import SchedoxProductMappingFields from '../SchedoxProductMappingFields';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

/**
 * Add Product Modal Component
 */
const AddProductModal = ({
  visible,
  onCancel,
  onSuccess,
  schedoxCatalog,
  schedoxCatalogLoading,
}) => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [fileList, setFileList] = useState([]);
    const [users, setUsers] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [isRentalProduct, setIsRentalProduct] = useState(false);
  const [isPack, setIsPack] = useState(false);
  
  // Fetch categories when modal opens
  useEffect(() => {
    if (visible) {
      fetchCategories();
      // Reset form when modal opens
      form.resetFields();
      setFileList([]);
      setIsRentalProduct(false);
      setIsPack(false);
    }
  }, [visible, form]);
  
  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      setCategories(response.data.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching categories',
        description: error.message || 'Failed to load categories'
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Prepare form data for API request
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(values).forEach(key => {
        // Skip rental pricing fields if not a rental product
        if (!isRentalProduct && ['priceone', 'pricetwo', 'pricethree', 'pricefour'].includes(key)) {
          return;
        }
        
        if (key === 'sync_to_schedox' || key === 'hidden_from_client_portal') {
          formData.append(key, values[key] ? '1' : '0');
          return;
        }

        // Skip image field as we'll handle it separately
        if (key === 'image') {
          return;
        }
        
        // Add field to form data
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });
      
      // Add is_pack flag
      formData.append('is_pack', isPack ? 1 : 0);
      formData.append('schedox_mapping_submitted', '1');
      
      // Add image if present
      if (fileList.length > 0) {
        formData.append('image', fileList[0].originFileObj || fileList[0]);
      }
      
      // Call API to create product
      await productService.createProduct(formData);
      
      notification.success({
        message: 'Product created successfully',
        description: 'The new product has been added to your inventory.'
      });
      
      // Reset form and close modal
      form.resetFields();
      setFileList([]);
      setIsRentalProduct(false);
      setIsPack(false);
      onSuccess();
      
    } catch (error) {
      notification.error({
        message: 'Failed to create product',
        description: error.message || 'Please check your inputs and try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle category type change to determine if rental product
  const handleCategoryTypeChange = (value) => {
    // Find if the selected category is a rental type
    const category = categories.find(c => c.id === value);
    if (category) {
      // Assuming category type ID 2 is for rental products
      const isRental = category.category_type_id === 2;
      setIsRentalProduct(isRental);
      
      // Reset rental prices if switching from rental to non-rental
      if (!isRental) {
        form.setFieldsValue({
          priceone: 0,
          pricetwo: 0,
          pricethree: 0,
          pricefour: 0
        });
      }
    }
  };

  // File upload handlers
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleBeforeUpload = (file) => {
    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      notification.error({
        message: 'Invalid file type',
        description: 'You can only upload image files!'
      });
      return Upload.LIST_IGNORE;
    }
    
    // Validate file size (2MB limit)
    const isLessThan2MB = file.size / 1024 / 1024 < 2;
    if (!isLessThan2MB) {
      notification.error({
        message: 'File too large',
        description: 'Image must be smaller than 2MB!'
      });
      return Upload.LIST_IGNORE;
    }
    
    return false; // Prevent auto upload
  };
  
  return (
    <Modal
      title="Add New Product"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          className="bg-golden-navy"
        >
          Create Product
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: '1',
            quantity: 0,
            product_price: 0,
            cost_price: 0,
            priceone: 0,
            pricetwo: 0,
            pricethree: 0,
            pricefour: 0,
            is_pack: false,
            sync_to_schedox: false,
            schedox_service_id: null,
            schedox_service_item_id: null,
            hidden_from_client_portal: false
          }}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="Basic Information" key="basic">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  name="product_title"
                  label="Product Title"
                  rules={[{ required: true, message: 'Please enter product title' }]}
                >
                  <Input placeholder="Enter product title" />
                </Form.Item>
                
                <Form.Item
                  name="product_code"
                  label="Product Code"
                  rules={[{ required: true, message: 'Please enter product code' }]}
                >
                  <Input placeholder="Enter product code" />
                </Form.Item>
                
                <Form.Item
                  name="category_id"
                  label="Category"
                  rules={[{ required: true, message: 'Please select a category' }]}
                >
                  <Select
                    placeholder="Select a category"
                    onChange={handleCategoryTypeChange}
                    showSearch
                    optionFilterProp="children"
                  >
                    {categories.map(category => (
                      <Option key={category.id} value={category.id}>
                        {category.category_title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                
                <Form.Item
                  name="quantity"
                  label="Quantity in Stock"
                  rules={[{ required: true, message: 'Please enter quantity' }]}
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
                
                <Form.Item
                  name="product_description"
                  label="Product Description"
                  className="col-span-2"
                >
                  <TextArea rows={4} placeholder="Enter product description" />
                </Form.Item>
                
                <Form.Item
                  name="status"
                  label="Status"
                  valuePropName="checked"
                  className="col-span-2"
                >
                  <Switch 
                    checkedChildren="Active" 
                    unCheckedChildren="Inactive" 
                    defaultChecked 
                  />
                </Form.Item>
                
                <Form.Item
                  label="Is Pack"
                  className="col-span-2"
                >
                  <Switch 
                    checked={isPack}
                    onChange={(checked) => setIsPack(checked)}
                    checkedChildren="Yes" 
                    unCheckedChildren="No" 
                  />
                </Form.Item>

                <SchedoxProductMappingFields
                  form={form}
                  catalog={schedoxCatalog}
                  loading={schedoxCatalogLoading}
                />

                <Form.Item
                  name="hidden_from_client_portal"
                  label="Client / agency portal visibility"
                  valuePropName="checked"
                  className="col-span-2"
                  extra="When enabled, this product remains available to admins but is hidden from client and agency ordering screens."
                >
                  <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
                </Form.Item>
              </div>
            </TabPane>
            
            <TabPane tab="Pricing" key="pricing">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isRentalProduct ? (
                  <>
                    <Form.Item
                      name="product_price"
                      label="Selling Price"
                      rules={[{ required: true, message: 'Please enter selling price' }]}
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="cost_price"
                      label="Cost Price"
                      rules={[{ required: true, message: 'Please enter cost price' }]}
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                  </>
                ) : (
                  <>
                    <div className="col-span-2 mb-4">
                      <Tag color="blue">Rental Product</Tag>
                      <p className="text-sm text-gray-500 mt-2">
                        Please set the rental prices for different durations below:
                      </p>
                    </div>
                    
                    <Form.Item
                      name="priceone"
                      label="Price (3 Days)"
                      rules={[{ required: true, message: 'Please enter 3 days price' }]}
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="pricetwo"
                      label="Price (1 Week)"
                      rules={[{ required: true, message: 'Please enter 1 week price' }]}
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="pricethree"
                      label="Price (2 Weeks)"
                      rules={[{ required: true, message: 'Please enter 2 weeks price' }]}
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="pricefour"
                      label="Price (1 Month)"
                      rules={[{ required: true, message: 'Please enter 1 month price' }]}
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="cost_price"
                      label="Cost Price"
                      rules={[{ required: true, message: 'Please enter cost price' }]}
                      className="col-span-2"
                    >
                      <InputNumber 
                        min={0} 
                        step={0.01} 
                        style={{ width: '100%' }} 
                        prefix="£" 
                      />
                    </Form.Item>
                  </>
                )}
              </div>
            </TabPane>
            
            <TabPane tab="Image" key="image">
              <div className="grid grid-cols-1 gap-4">
                <Form.Item
                  label="Product Image"
                  name="image"
                  className="text-center"
                >
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    onChange={handleFileChange}
                    beforeUpload={handleBeforeUpload}
                    maxCount={1}
                    accept="image/*"
                  >
                    {fileList.length === 0 && (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
                
                <div className="text-center text-sm text-gray-500">
                  <p>Recommended image size: 640x640 pixels</p>
                  <p>Maximum file size: 2MB</p>
                  <p>Supported formats: JPG, PNG, GIF</p>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddProductModal;
